import { useMemo } from 'react';
import type { ChangeEvent } from 'react';
import type { RouteAnalyticsView } from '../../../domain/models/contracts';

interface RouteSelectorProps {
  readonly routes: readonly RouteAnalyticsView[];
  readonly selectedLegId: string;
  readonly onSelect: (legId: string) => void;
}

interface RouteOption {
  readonly legId: string;
  readonly label: string;
}

function buildOptions(routes: readonly RouteAnalyticsView[]): readonly RouteOption[] {
  const pairCounts = new Map<string, number>();
  for (const route of routes) {
    const pair = `${route.origin}–${route.destination}`;
    pairCounts.set(pair, (pairCounts.get(pair) ?? 0) + 1);
  }
  return routes
    .map((route) => {
      const pair = `${route.origin}–${route.destination}`;
      // Disambiguate when several legs serve the same O&D pair.
      const label = (pairCounts.get(pair) ?? 0) > 1 ? `${pair} (${route.legId})` : pair;
      return { legId: route.legId, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function RouteSelector({ routes, selectedLegId, onSelect }: RouteSelectorProps) {
  const options = useMemo(() => buildOptions(routes), [routes]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    onSelect(event.target.value);
  };

  return (
    <label className="ri-selector">
      <span className="ri-selector__label">Route</span>
      <select
        className="ri-selector__select"
        value={selectedLegId}
        onChange={handleChange}
        aria-label="Select route"
      >
        {options.map((option) => (
          <option key={option.legId} value={option.legId}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
